/**
 * Real-time collaboration types for Deep Agents platform.
 *
 * These types define the structure for multi-user presence, cursor positions,
 * and awareness state used across the Yjs-based collaboration system.
 */

/**
 * User information displayed in presence indicators.
 *
 * This represents a single user in the collaborative session.
 * Color is auto-assigned from CURSOR_COLORS palette on connection.
 */
export interface PresenceUser {
  /** User ID from authentication system */
  id: string;

  /** Display name (first name only per CONTEXT.md) */
  name: string;

  /** Hex color from CURSOR_COLORS palette (e.g., '#E57373') */
  color: string;

  /** Optional avatar URL for profile picture */
  avatarUrl?: string;
}

/**
 * Cursor position coordinates on the canvas.
 *
 * Origin (0,0) is top-left of canvas viewport.
 * Coordinates are in canvas space (not screen space).
 */
export interface CursorPosition {
  /** Horizontal position in pixels */
  x: number;

  /** Vertical position in pixels */
  y: number;
}

/**
 * Text cursor selection stored by Tiptap/Yjs collaboration.
 *
 * These values are Yjs relative positions serialized by y-prosemirror/y-tiptap.
 */
export interface TextCursorSelection {
  /** Anchor position (relative) */
  anchor: unknown;

  /** Head position (relative) */
  head: unknown;
}

/**
 * User's current selection state.
 *
 * Tracks what content the user has selected for visual indication
 * to other collaborators (colored borders/outlines).
 *
 * Phase 4/5 will populate this when canvas and text editing exist.
 */
export interface Selection {
  /** What type of content is selected */
  type: 'canvas' | 'text' | null;

  /** Selected canvas node IDs (for canvas selection) */
  ids?: string[];

  /** Text selection anchor position (for text selection) */
  anchor?: unknown;

  /** Text selection head position (for text selection) */
  head?: unknown;
}

/**
 * User's current viewport (visible area).
 *
 * Used for "follow mode" where clicking an avatar jumps to
 * and follows that user's viewport position.
 */
export interface Viewport {
  /** Viewport center X coordinate */
  x: number;

  /** Viewport center Y coordinate */
  y: number;

  /** Zoom level (1.0 = 100%, 2.0 = 200%, 0.5 = 50%) */
  zoom: number;
}

/**
 * Complete awareness state for a single client.
 *
 * This is what gets stored in Yjs Awareness CRDT and broadcast
 * to all connected clients. Updated on cursor movement, selection
 * changes, and viewport changes.
 *
 * Awareness state has 30-second timeout - must be refreshed
 * every 15-20 seconds to prevent auto-removal.
 */
export interface AwarenessState {
  /** User information (ID, name, color) */
  user: PresenceUser;

  /** Text cursor/selection for the rich text editor */
  cursor?: TextCursorSelection | null;

  /** Canvas cursor position (null if cursor not on canvas) */
  canvasCursor?: CursorPosition | null;

  /** Current selection state (null if nothing selected) */
  selection: Selection | null;

  /** Current viewport (optional, for follow mode) */
  viewport?: Viewport;
}

/**
 * WebSocket connection status.
 *
 * Used to display connection state UI:
 * - connecting: Initial connection attempt
 * - connected: Normal state (no UI indicator per CONTEXT.md)
 * - disconnected: Show modal with retry button
 * - readonly: Permanent failure, read-only mode
 */
export type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'readonly';

/**
 * Yjs binary protocol message types.
 *
 * These match the y-protocols message type identifiers:
 * - sync-step-1: Request state vector (initial sync)
 * - sync-step-2: Send missing updates (sync response)
 * - sync-update: Incremental update broadcast
 * - awareness: Awareness state update (cursors, presence)
 */
export type YjsMessageType = 'sync-step-1' | 'sync-step-2' | 'sync-update' | 'awareness';

/**
 * Predefined cursor color palette.
 *
 * 8 distinct colors meeting WCAG 4.5:1 contrast requirements
 * for accessibility on both light and dark backgrounds.
 *
 * Colors are Material Design 300-level shades for softer appearance.
 * Auto-assigned cyclically based on client ID (clientId % 8).
 */
export const CURSOR_COLORS = [
  '#E57373', // Red 300
  '#64B5F6', // Blue 300
  '#81C784', // Green 300
  '#FFB74D', // Orange 300
  '#BA68C8', // Purple 300
  '#4DD0E1', // Cyan 300
  '#F06292', // Pink 300
  '#AED581', // Light Green 300
] as const;

/**
 * WebSocket message wrapper for Yjs protocol messages.
 *
 * All messages sent over WebSocket are prefixed with a message type
 * byte followed by the protocol-specific payload.
 */
export interface YjsMessage {
  /** Message type identifier */
  type: YjsMessageType;

  /** Binary payload (Uint8Array encoded by y-protocols) */
  payload: Uint8Array;
}

// ============================================================================
// AI PRESENCE TYPES (Phase 7 - Ghost Writer)
// ============================================================================

/**
 * Special client ID for AI agent presence.
 *
 * Uses 999999 to avoid collision with normal Yjs client IDs.
 * Yjs client IDs are typically generated from timestamps or random values,
 * but this reserved ID ensures AI always has a unique, predictable identifier.
 */
export const AI_CLIENT_ID = 999999;

/**
 * Awareness state for the AI agent collaborator.
 *
 * Similar to AwarenessState but with AI-specific fields for tracking
 * whether the AI is currently thinking (processing) or typing (streaming).
 *
 * The AI appears as a real collaborator with cursor movement and presence
 * in the avatar stack.
 */
export interface AIAwarenessState {
  /** AI user info (constant values, violet theme color) */
  user: {
    /** Fixed ID for AI agent */
    id: 'ai-agent';
    /** Display name shown on cursor label */
    name: string;
    /** Violet theme color (#8B5CF6) matching AI node styling */
    color: string;
  };

  /** Canvas cursor position when AI is working on canvas nodes */
  canvasCursor: { x: number; y: number } | null;

  /** Text cursor position when AI is typing in a text field */
  textCursor: { field: string; index: number } | null;

  /** True when AI is processing/generating (pulsing indicator) */
  isThinking: boolean;

  /** True when AI is streaming text output (typing indicator) */
  isTyping: boolean;

  /** Optional description of current task (shown in indicator) */
  taskDescription?: string;
}

/**
 * WebSocket message type for requesting AI actions.
 *
 * Sent from frontend to Board DO when user triggers AI action
 * (e.g., selecting text and choosing "Rewrite").
 */
export interface AIRequestMessage {
  /** Message type identifier for WebSocket routing */
  type: 'ai_request';

  /** What action the AI should perform */
  action: 'generate' | 'rewrite' | 'summarize' | 'expand' | 'simplify' | 'formal' | 'casual' | 'professional';

  /** Where the AI should write output */
  targetType: 'canvas' | 'document';

  /** For document: which Y.Text or Y.XmlFragment to modify */
  targetField?: string;

  /** For canvas: which node to create or modify */
  targetNodeId?: string;

  /** Selected text or surrounding context for AI to work with */
  context: string;

  /** For canvas: where to create new node (if applicable) */
  position?: { x: number; y: number };
}
